import { ChangeEvent, CSSProperties, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { MemberRole, SiteAuthProfile, SiteAuthProfileTestResult } from '@accestra/types';
import { api, fetchAuthenticated, friendlyError } from '../../lib/api-client';
import { buildBillingUpgradePath } from '../../lib/plans';
import { formatDateTime } from '../../i18n/formatters';
import LockedFeatureState from '../entitlements/LockedFeatureState';
import AlertBanner from '../ui/AlertBanner';
import Button from '../ui/Button';
import Card from '../ui/Card';

type EditableAuthMode = 'bearer_token' | 'session_storage_state' | 'form_login';

interface AuthenticatedScanAccessCardProps {
  siteId: string;
  planLabel: string;
  featureEnabled: boolean;
  role: MemberRole | null;
}

const UPGRADE_PATH = buildBillingUpgradePath('growth', 'authenticated-scan-lock');

const inputS: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid var(--input-border)',
  borderRadius: 8,
  padding: '9px 12px',
  fontSize: 14,
  color: 'var(--acc-color-text)',
  background: 'var(--input-bg)',
};

const labelS: CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--acc-color-text-muted)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  marginBottom: 6,
};

const helperTextS: CSSProperties = {
  marginTop: 6,
  fontSize: 12,
  color: 'var(--acc-color-text-muted)',
};

const infoPanelS: CSSProperties = {
  border: '1px solid var(--acc-color-border)',
  borderRadius: 10,
  background: 'var(--surface-secondary)',
  padding: '14px 16px',
  display: 'grid',
  gap: 6,
};

const infoPanelTitleS: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--acc-color-text-soft)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

function testStatusVariant(status: SiteAuthProfileTestResult['status'] | SiteAuthProfile['lastTestStatus']) {
  switch (status) {
    case 'passed': return 'info';
    case 'failed': return 'danger';
    case 'invalid': return 'warning';
    default: return 'neutral';
  }
}

function testStatusLabel(status: SiteAuthProfile['lastTestStatus'], t: TranslateFn) {
  switch (status) {
    case 'passed': return t('authProfile.status.passed');
    case 'failed': return t('authProfile.status.failed');
    case 'invalid': return t('authProfile.status.invalid');
    default: return t('authProfile.status.notTested');
  }
}

function maskedHint(configured: boolean, replacementLabel: string, t: TranslateFn) {
  return configured
    ? t('authProfile.masked.configured', { label: replacementLabel })
    : t('authProfile.masked.notConfigured');
}

function readinessLabel(profile: SiteAuthProfile | null, t: TranslateFn) {
  if (!profile?.isEnabled) return t('authProfile.readiness.disabled');
  if (profile.lastTestStatus === 'passed') return t('authProfile.readiness.ready');
  if (profile.lastTestStatus === 'failed') return t('authProfile.readiness.notReady');
  if (profile.lastTestStatus === 'invalid') return t('authProfile.readiness.notValidated');
  return t('authProfile.readiness.notValidated');
}

function formatFailureCategory(
  category: SiteAuthProfile['lastTestFailureCategory'] | SiteAuthProfile['lastScanAuthFailureCategory'],
  t: TranslateFn,
) {
  if (!category) return null;
  switch (category) {
    case 'session_expired': return t('auth.failures.sessionExpired');
    case 'redirected_to_login_mid_scan': return t('auth.failures.redirectedToLoginMidScan');
    case 'auth_session_invalidated': return t('auth.failures.authSessionInvalidated');
    case 'redirected_to_login': return t('auth.failures.redirectedToLogin');
    case 'selector_missing': return t('auth.failures.selectorMissing');
    case 'success_selector_not_found': return t('auth.failures.successSelectorNotFound');
    case 'success_url_pattern_not_matched': return t('auth.failures.successUrlPatternNotMatched');
    case 'selector_not_found': return t('auth.failures.selectorNotFound');
    case 'auth_token_rejected': return t('auth.failures.authTokenRejected');
    default: return category.replace(/_/g, ' ');
  }
}

function failureCategoryDetail(
  category: SiteAuthProfile['lastTestFailureCategory'] | SiteAuthProfile['lastScanAuthFailureCategory'],
  t: TranslateFn,
) {
  switch (category) {
    case 'session_expired':
      return t('authProfile.failureDetails.sessionExpired');
    case 'redirected_to_login_mid_scan':
      return t('authProfile.failureDetails.redirectedToLoginMidScan');
    case 'auth_session_invalidated':
      return t('authProfile.failureDetails.authSessionInvalidated');
    case 'selector_missing':
      return t('authProfile.failureDetails.selectorMissing');
    case 'selector_not_found':
      return t('authProfile.failureDetails.selectorNotFound');
    case 'success_selector_not_found':
      return t('authProfile.failureDetails.successSelectorNotFound');
    case 'success_url_pattern_not_matched':
      return t('authProfile.failureDetails.successUrlPatternNotMatched');
    case 'auth_token_rejected':
      return t('authProfile.failureDetails.authTokenRejected');
    case 'redirected_to_login':
      return t('authProfile.failureDetails.redirectedToLogin');
    default:
      return null;
  }
}

function refreshDetail(attempted: boolean, succeeded: boolean, t: TranslateFn) {
  if (!attempted) return null;
  return succeeded
    ? t('authProfile.refresh.succeeded')
    : t('authProfile.refresh.failed');
}

function shouldShowDiagnosticBundle(
  status: SiteAuthProfile['lastTestStatus'] | SiteAuthProfile['lastScanAuthStatus'],
  screenshotAvailable: boolean,
) {
  return status === 'failed' || status === 'invalid' || screenshotAvailable;
}

type DiagnosticScope = 'test' | 'scan';

function diagnosticSummary(
  scope: DiagnosticScope,
  status: SiteAuthProfile['lastTestStatus'] | SiteAuthProfile['lastScanAuthStatus'],
  t: TranslateFn,
) {
  if (scope === 'test') {
    switch (status) {
      case 'passed':
        return t('authProfile.results.testPassed');
      case 'failed':
        return t('authProfile.results.testFailed');
      case 'invalid':
        return t('authProfile.results.testInvalid');
      default:
        return t('authProfile.results.noneRecorded');
    }
  }

  switch (status) {
    case 'passed':
      return t('authProfile.results.crawlPassed');
    case 'failed':
      return t('authProfile.results.crawlFailed');
    case 'invalid':
      return t('authProfile.results.crawlInvalid');
    default:
      return t('authProfile.results.crawlNoneRecorded');
  }
}

function formatProfileDateTime(value: string | Date | null | undefined, t: TranslateFn) {
  return formatDateTime(value) || t('common:labels.notAvailable');
}

export default function AuthenticatedScanAccessCard({
  siteId,
  planLabel,
  featureEnabled,
  role,
}: AuthenticatedScanAccessCardProps) {
  const { t } = useTranslation(['site', 'common']);
  const navigate = useNavigate();
  const canManage = role === 'owner' || role === 'admin';

  const [loading, setLoading] = useState(canManage);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [openingScreenshotScope, setOpeningScreenshotScope] = useState<DiagnosticScope | null>(null);
  const [downloadingDiagnosticScope, setDownloadingDiagnosticScope] = useState<DiagnosticScope | null>(null);
  const [previewScope, setPreviewScope] = useState<DiagnosticScope | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profile, setProfile] = useState<SiteAuthProfile | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [authMode, setAuthMode] = useState<EditableAuthMode>('bearer_token');
  const [loginUrl, setLoginUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [bearerToken, setBearerToken] = useState('');
  const [customHeadersJson, setCustomHeadersJson] = useState('');
  const [storageStateJson, setStorageStateJson] = useState('');
  const [storageStateFileName, setStorageStateFileName] = useState<string | null>(null);
  const [authSuccessSelector, setAuthSuccessSelector] = useState('');
  const [authSuccessUrlPattern, setAuthSuccessUrlPattern] = useState('');
  const [usernameSelector, setUsernameSelector] = useState('');
  const [passwordSelector, setPasswordSelector] = useState('');
  const [submitSelector, setSubmitSelector] = useState('');
  const [successSelector, setSuccessSelector] = useState('');
  const [notes, setNotes] = useState('');

  function resetSecretInputs() {
    setPassword('');
    setBearerToken('');
    setCustomHeadersJson('');
    setStorageStateJson('');
    setStorageStateFileName(null);
  }

  function clearPreview() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewScope(null);
    setPreviewUrl(null);
  }

  function applyProfile(nextProfile: SiteAuthProfile | null) {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewScope(null);
    setPreviewUrl(null);
    setProfile(nextProfile);
    setEnabled(nextProfile?.isEnabled ?? false);
    setAuthMode(
      nextProfile?.authMode && nextProfile.authMode !== 'none'
        ? nextProfile.authMode
        : 'bearer_token',
    );
    setLoginUrl(nextProfile?.loginUrl ?? '');
    setUsername(nextProfile?.username ?? '');
    setAuthSuccessSelector(nextProfile?.authSuccessSelector ?? '');
    setAuthSuccessUrlPattern(nextProfile?.authSuccessUrlPattern ?? '');
    setUsernameSelector(nextProfile?.usernameSelector ?? '');
    setPasswordSelector(nextProfile?.passwordSelector ?? '');
    setSubmitSelector(nextProfile?.submitSelector ?? '');
    setSuccessSelector(nextProfile?.successSelector ?? '');
    setNotes(nextProfile?.notes ?? '');
    resetSecretInputs();
  }

  useEffect(() => () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);

  useEffect(() => {
    if (!canManage || !featureEnabled) {
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    api.get<{ profile: SiteAuthProfile | null }>(`/sites/${siteId}/auth-profile`)
      .then((data) => {
        if (!active) return;
        applyProfile(data.profile);
      })
      .catch((err) => {
        if (!active) return;
        setError(friendlyError(err, t('authProfile.feedback.loadFailed')));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [canManage, featureEnabled, siteId, t]);

  async function handleStorageStateFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      setStorageStateJson(text);
      setStorageStateFileName(file.name);
      setSuccess(null);
      setError(null);
    } catch {
      setError(t('authProfile.feedback.fileReadFailed'));
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload: Record<string, unknown> = {
        authMode,
        isEnabled: enabled,
        notes: notes.trim() || null,
      };

      if (authMode === 'bearer_token') {
        if (bearerToken.trim()) payload['bearerToken'] = bearerToken.trim();
        if (customHeadersJson.trim()) payload['customHeadersJson'] = customHeadersJson.trim();
      }

      if (authMode === 'session_storage_state') {
        if (storageStateJson.trim()) payload['storageStateJson'] = storageStateJson.trim();
        payload['authSuccessSelector'] = authSuccessSelector.trim() || null;
        payload['authSuccessUrlPattern'] = authSuccessUrlPattern.trim() || null;
      }

      if (authMode === 'form_login') {
        payload['loginUrl'] = loginUrl.trim() || null;
        payload['username'] = username.trim() || null;
        payload['usernameSelector'] = usernameSelector.trim() || null;
        payload['passwordSelector'] = passwordSelector.trim() || null;
        payload['submitSelector'] = submitSelector.trim() || null;
        payload['successSelector'] = successSelector.trim() || null;
        if (password.trim()) payload['password'] = password;
      }

      const data = await api.put<{ profile: SiteAuthProfile }>(
        `/sites/${siteId}/auth-profile`,
        payload,
      );

      applyProfile(data.profile);
      setSuccess(t('authProfile.feedback.saveSuccess'));
    } catch (err) {
      setError(friendlyError(err, t('authProfile.feedback.saveFailed')));
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await api.post<{ result: SiteAuthProfileTestResult; profile: SiteAuthProfile }>(
        `/sites/${siteId}/auth-profile/test`,
      );
      applyProfile(data.profile);
      if (data.result.status === 'passed') {
        setSuccess(t('authProfile.feedback.testPassed'));
      } else {
        setError(t('authProfile.feedback.testFailed'));
      }
    } catch (err) {
      setError(friendlyError(err, t('authProfile.feedback.testFailed')));
    } finally {
      setTesting(false);
    }
  }

  async function handleRemove() {
    if (!window.confirm(t('authProfile.feedback.removeConfirm'))) {
      return;
    }

    setRemoving(true);
    setError(null);
    setSuccess(null);

    try {
      await api.delete(`/sites/${siteId}/auth-profile`);
      applyProfile(null);
      setSuccess(t('authProfile.feedback.removeSuccess'));
    } catch (err) {
      setError(friendlyError(err, t('authProfile.feedback.removeFailed')));
    } finally {
      setRemoving(false);
    }
  }

  async function handlePreviewDiagnosticScreenshot(scope: DiagnosticScope) {
    setOpeningScreenshotScope(scope);
    setError(null);

    try {
      const response = await fetchAuthenticated(`/sites/${siteId}/auth-profile/diagnostics/${scope}/screenshot`);
      if (!response.ok) {
        const message =
          response.status === 404
            ? t('authProfile.feedback.screenshotNotFound')
            : response.status === 403
              ? t('authProfile.feedback.screenshotForbidden')
              : t('authProfile.feedback.screenshotHttpError', { status: response.status });
        setError(message);
        return;
      }

      const blob = await response.blob();
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewScope(scope);
    } catch {
      setError(t('authProfile.feedback.screenshotOpenFailed'));
    } finally {
      setOpeningScreenshotScope(null);
    }
  }

  async function handleDownloadDiagnostics(scope: DiagnosticScope) {
    setDownloadingDiagnosticScope(scope);
    setError(null);

    try {
      const response = await fetchAuthenticated(`/sites/${siteId}/auth-profile/diagnostics/${scope}/bundle`);
      if (!response.ok) {
        const message =
          response.status === 404
            ? t('authProfile.feedback.bundleNotFound')
            : response.status === 403
              ? t('authProfile.feedback.bundleForbidden')
              : t('authProfile.feedback.bundleHttpError', { status: response.status });
        setError(message);
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers.get('Content-Disposition');
      const match = contentDisposition?.match(/filename="([^"]+)"/);
      link.download = match?.[1] ?? `site-auth-diagnostics-${scope}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError(t('authProfile.feedback.bundleDownloadFailed'));
    } finally {
      setDownloadingDiagnosticScope(null);
    }
  }

  if (!featureEnabled) {
    return (
      <Card title={t('authProfile.title')}>
        <LockedFeatureState
          withCard={false}
          tierLabel={t('authProfile.locked.tierLabel')}
          title={t('authProfile.locked.title')}
          description={t('authProfile.locked.description', { plan: planLabel })}
          benefits={[
            {
              title: t('authProfile.locked.benefits.secureSecrets.title'),
              body: t('authProfile.locked.benefits.secureSecrets.body'),
            },
            {
              title: t('authProfile.locked.benefits.protectedCoverage.title'),
              body: t('authProfile.locked.benefits.protectedCoverage.body'),
            },
            {
              title: t('authProfile.locked.benefits.operationalValidation.title'),
              body: t('authProfile.locked.benefits.operationalValidation.body'),
            },
          ]}
          upgradeLabel={t('authProfile.locked.upgradeLabel')}
          onUpgrade={() => navigate(UPGRADE_PATH)}
          onPricing={() => navigate('/pricing')}
        />
      </Card>
    );
  }

  if (!canManage) {
    return (
      <Card title={t('authProfile.title')}>
        <AlertBanner variant="neutral" icon="ℹ">
          {t('authProfile.notAuthorized')}
        </AlertBanner>
      </Card>
    );
  }

  return (
    <Card title={t('authProfile.title')}>
      <div style={{ display: 'grid', gap: 16 }}>
        <AlertBanner variant="info" icon="🔐">
          {t('authProfile.notice.security')}
        </AlertBanner>

        <div style={infoPanelS}>
          <div style={infoPanelTitleS}>
            {t('authProfile.guidance.title')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--acc-color-text)' }}>
            {t('authProfile.guidance.bearerToken')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--acc-color-text)' }}>
            {t('authProfile.guidance.sessionStorageState')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--acc-color-text)' }}>
            {t('authProfile.guidance.formLogin')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--acc-color-text-muted)' }}>
            {t('authProfile.guidance.mfa')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--acc-color-text-muted)' }}>
            {t('authProfile.guidance.failures')}
          </div>
        </div>

        {error && (
          <AlertBanner variant="danger" icon="✕">
            {error}
          </AlertBanner>
        )}

        {success && (
          <AlertBanner variant="info" icon="✓">
            {success}
          </AlertBanner>
        )}

        {loading ? (
          <div style={{ color: 'var(--acc-color-text-muted)', fontSize: 14 }}>{t('authProfile.loading')}</div>
        ) : (
          <>
            <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <div>
                <label style={labelS}>{t('authProfile.fields.access')}</label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer', color: 'var(--acc-color-text)', fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(event) => setEnabled(event.target.checked)}
                    style={{ width: 16, height: 16, accentColor: 'var(--acc-color-primary)' }}
                  />
                  {t('authProfile.fields.enable')}
                </label>
              </div>

              <div>
                <label style={labelS}>{t('authProfile.fields.mode')}</label>
                <select
                  value={authMode}
                  onChange={(event) => setAuthMode(event.target.value as EditableAuthMode)}
                  style={inputS}
                >
                  <option value="bearer_token">{t('auth.modes.bearerToken')}</option>
                  <option value="session_storage_state">{t('auth.modes.sessionStorageState')}</option>
                  <option value="form_login">{t('auth.modes.formLogin')}</option>
                </select>
              </div>
            </div>

            {authMode === 'bearer_token' && (
              <div style={{ display: 'grid', gap: 14 }}>
                <div>
                  <label style={labelS}>{t('authProfile.fields.bearerToken')}</label>
                  <input
                    type="password"
                    autoComplete="off"
                    placeholder={t('authProfile.fields.placeholders.bearerToken')}
                    value={bearerToken}
                    onChange={(event) => setBearerToken(event.target.value)}
                    style={inputS}
                  />
                  <div style={helperTextS}>
                    {maskedHint(profile?.secrets.bearerTokenConfigured ?? false, t('authProfile.fields.bearerToken'), t)}
                  </div>
                </div>

                <div>
                  <label style={labelS}>{t('authProfile.fields.customHeaders')}</label>
                  <textarea
                    value={customHeadersJson}
                    onChange={(event) => setCustomHeadersJson(event.target.value)}
                    placeholder='{"X-Environment":"staging"}'
                    style={{ ...inputS, minHeight: 96, resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }}
                  />
                  <div style={helperTextS}>
                    {maskedHint(profile?.secrets.customHeadersConfigured ?? false, t('authProfile.fields.customHeadersLabel'), t)}
                  </div>
                </div>
              </div>
            )}

            {authMode === 'session_storage_state' && (
              <div style={{ display: 'grid', gap: 14 }}>
                <div>
                  <label style={labelS}>{t('authProfile.fields.storageState')}</label>
                  <textarea
                    value={storageStateJson}
                    onChange={(event) => setStorageStateJson(event.target.value)}
                    placeholder='{"cookies":[],"origins":[]}'
                    style={{ ...inputS, minHeight: 180, resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }}
                  />
                  <div style={helperTextS}>
                    {maskedHint(profile?.secrets.storageStateConfigured ?? false, t('authProfile.fields.storageStateLabel'), t)}
                    {storageStateFileName ? ` ${t('authProfile.masked.loadedFrom', { fileName: storageStateFileName })}` : ''}
                  </div>
                </div>

                <div>
                  <label style={labelS}>{t('authProfile.fields.importStorageState')}</label>
                  <input type="file" accept=".json,application/json" onChange={(event) => void handleStorageStateFileChange(event)} />
                </div>

                <div>
                  <label style={labelS}>{t('authProfile.fields.authSuccessSelector')}</label>
                  <input
                    type="text"
                    value={authSuccessSelector}
                    onChange={(event) => setAuthSuccessSelector(event.target.value)}
                    placeholder='[data-testid="app-shell"]'
                    style={inputS}
                  />
                  <div style={helperTextS}>
                    {t('authProfile.fields.hints.authSuccessSelector')}
                  </div>
                </div>

                <div>
                  <label style={labelS}>{t('authProfile.fields.authSuccessUrlPattern')}</label>
                  <input
                    type="text"
                    value={authSuccessUrlPattern}
                    onChange={(event) => setAuthSuccessUrlPattern(event.target.value)}
                    placeholder="https://app.example.com/app/*"
                    style={inputS}
                  />
                  <div style={helperTextS}>
                    {t('authProfile.fields.hints.authSuccessUrlPattern')}
                  </div>
                </div>
              </div>
            )}

            {authMode === 'form_login' && (
              <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelS}>{t('authProfile.fields.loginUrl')}</label>
                  <input
                    type="url"
                    value={loginUrl}
                    onChange={(event) => setLoginUrl(event.target.value)}
                    placeholder="https://app.example.com/login"
                    style={inputS}
                  />
                </div>

                <div>
                  <label style={labelS}>{t('authProfile.fields.username')}</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    placeholder="test.user@example.com"
                    style={inputS}
                  />
                </div>

                <div>
                  <label style={labelS}>{t('authProfile.fields.password')}</label>
                  <input
                    type="password"
                    autoComplete="off"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder={t('authProfile.fields.placeholders.password')}
                    style={inputS}
                  />
                  <div style={helperTextS}>
                    {maskedHint(profile?.secrets.passwordConfigured ?? false, t('authProfile.fields.password'), t)}
                  </div>
                </div>

                <div>
                  <label style={labelS}>{t('authProfile.fields.usernameSelector')}</label>
                  <input
                    type="text"
                    value={usernameSelector}
                    onChange={(event) => setUsernameSelector(event.target.value)}
                    placeholder='input[name="email"]'
                    style={inputS}
                  />
                </div>

                <div>
                  <label style={labelS}>{t('authProfile.fields.passwordSelector')}</label>
                  <input
                    type="text"
                    value={passwordSelector}
                    onChange={(event) => setPasswordSelector(event.target.value)}
                    placeholder='input[name="password"]'
                    style={inputS}
                  />
                </div>

                <div>
                  <label style={labelS}>{t('authProfile.fields.submitSelector')}</label>
                  <input
                    type="text"
                    value={submitSelector}
                    onChange={(event) => setSubmitSelector(event.target.value)}
                    placeholder='button[type="submit"]'
                    style={inputS}
                  />
                </div>

                <div>
                  <label style={labelS}>{t('authProfile.fields.successSelector')}</label>
                  <input
                    type="text"
                    value={successSelector}
                    onChange={(event) => setSuccessSelector(event.target.value)}
                    placeholder='[data-testid="app-shell"]'
                    style={inputS}
                  />
                </div>
              </div>
            )}

            <div>
              <label style={labelS}>{t('authProfile.fields.notes')}</label>
              <textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder={t('authProfile.fields.placeholders.notes')}
                style={{ ...inputS, minHeight: 96, resize: 'vertical' }}
              />
            </div>

            <div style={infoPanelS}>
              <div style={infoPanelTitleS}>
                {t('authProfile.readiness.title')}
              </div>
              <div style={{ fontSize: 14, color: 'var(--acc-color-text)' }}>
                {t('authProfile.readiness.statusLabel')} <strong>{readinessLabel(profile, t)}</strong>
              </div>
              <div style={{ fontSize: 13, color: 'var(--acc-color-text-muted)' }}>
                {profile?.lastTestStatus === 'passed'
                  ? t('authProfile.readiness.passedBody')
                  : profile?.isEnabled
                    ? t('authProfile.readiness.enabledBody')
                    : t('authProfile.readiness.disabledBody')}
              </div>
            </div>

            <div style={infoPanelS}>
              <div style={infoPanelTitleS}>
                {t('authProfile.results.lastTestTitle')}
              </div>
              <div style={{ fontSize: 14, color: 'var(--acc-color-text)' }}>
                {t('authProfile.results.statusLabel')} <strong>{testStatusLabel(profile?.lastTestStatus ?? null, t)}</strong>
              </div>
              <div style={{ fontSize: 13, color: 'var(--acc-color-text-muted)' }}>
                {t('authProfile.results.testedAt')} {formatProfileDateTime(profile?.lastTestedAt ?? null, t)}
              </div>
              <AlertBanner
                variant={testStatusVariant(profile?.lastTestStatus ?? null)}
                icon={profile?.lastTestStatus === 'passed' ? '✓' : profile?.lastTestStatus === 'failed' ? '✕' : 'ℹ'}
                style={{ marginTop: 6 }}
              >
                {diagnosticSummary('test', profile?.lastTestStatus ?? null, t)}
              </AlertBanner>
              {profile?.lastTestFailureCategory && (
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-soft)' }}>
                  {t('authProfile.results.failureCategory')} <strong>{formatFailureCategory(profile.lastTestFailureCategory, t)}</strong>
                </div>
              )}
              {profile?.lastTestFailureCategory && failureCategoryDetail(profile.lastTestFailureCategory, t) && (
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)' }}>
                  {failureCategoryDetail(profile.lastTestFailureCategory, t)}
                </div>
              )}
              {profile?.lastTestConsoleHint && (
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)' }}>
                  {t('authProfile.results.consoleHint', { hint: profile.lastTestConsoleHint })}
                </div>
              )}
              {profile?.lastTestNetworkHint && (
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)' }}>
                  {t('authProfile.results.networkHint', { hint: profile.lastTestNetworkHint })}
                </div>
              )}
              {refreshDetail(
                profile?.lastTestRefreshAttempted ?? false,
                profile?.lastTestRefreshSucceeded ?? false,
                t,
              ) && (
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)' }}>
                  {refreshDetail(
                    profile?.lastTestRefreshAttempted ?? false,
                    profile?.lastTestRefreshSucceeded ?? false,
                    t,
                  )}
                </div>
              )}
              {profile?.lastTestScreenshotAvailable && (
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-soft)' }}>
                  {t('authProfile.results.testScreenshotAvailable')}
                </div>
              )}
              {(profile?.lastTestScreenshotAvailable || shouldShowDiagnosticBundle(profile?.lastTestStatus ?? null, profile?.lastTestScreenshotAvailable ?? false)) && (
                <div style={{ marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {profile?.lastTestScreenshotAvailable && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => void handlePreviewDiagnosticScreenshot('test')}
                      disabled={openingScreenshotScope !== null}
                    >
                      {openingScreenshotScope === 'test' ? t('authProfile.actions.loadingPreview') : t('authProfile.actions.previewScreenshot')}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void handleDownloadDiagnostics('test')}
                    disabled={downloadingDiagnosticScope !== null}
                  >
                    {downloadingDiagnosticScope === 'test' ? t('authProfile.actions.downloading') : t('authProfile.actions.downloadDiagnostics')}
                  </Button>
                </div>
              )}
            </div>

            <div style={infoPanelS}>
              <div style={infoPanelTitleS}>
                {t('authProfile.results.lastCrawlTitle')}
              </div>
              <div style={{ fontSize: 14, color: 'var(--acc-color-text)' }}>
                {t('authProfile.results.statusLabel')} <strong>{testStatusLabel(profile?.lastScanAuthStatus ?? null, t)}</strong>
              </div>
              <div style={{ fontSize: 13, color: 'var(--acc-color-text-muted)' }}>
                {t('authProfile.results.observedAt')} {formatProfileDateTime(profile?.lastScanAuthAt ?? null, t)}
              </div>
              <AlertBanner
                variant={testStatusVariant(profile?.lastScanAuthStatus ?? null)}
                icon={profile?.lastScanAuthStatus === 'passed' ? '✓' : profile?.lastScanAuthStatus === 'failed' ? '✕' : 'ℹ'}
                style={{ marginTop: 6 }}
              >
                {diagnosticSummary('scan', profile?.lastScanAuthStatus ?? null, t)}
              </AlertBanner>
              {profile?.lastScanAuthFailureCategory && (
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-soft)' }}>
                  {t('authProfile.results.failureCategory')} <strong>{formatFailureCategory(profile.lastScanAuthFailureCategory, t)}</strong>
                </div>
              )}
              {profile?.lastScanAuthFailureCategory && failureCategoryDetail(profile.lastScanAuthFailureCategory, t) && (
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)' }}>
                  {failureCategoryDetail(profile.lastScanAuthFailureCategory, t)}
                </div>
              )}
              {profile?.lastScanAuthConsoleHint && (
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)' }}>
                  {t('authProfile.results.consoleHint', { hint: profile.lastScanAuthConsoleHint })}
                </div>
              )}
              {profile?.lastScanAuthNetworkHint && (
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)' }}>
                  {t('authProfile.results.networkHint', { hint: profile.lastScanAuthNetworkHint })}
                </div>
              )}
              {refreshDetail(
                profile?.lastScanAuthRefreshAttempted ?? false,
                profile?.lastScanAuthRefreshSucceeded ?? false,
                t,
              ) && (
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)' }}>
                  {refreshDetail(
                    profile?.lastScanAuthRefreshAttempted ?? false,
                    profile?.lastScanAuthRefreshSucceeded ?? false,
                    t,
                  )}
                </div>
              )}
              {profile?.lastScanAuthScreenshotAvailable && (
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-soft)' }}>
                  {t('authProfile.results.scanScreenshotAvailable')}
                </div>
              )}
              {(profile?.lastScanAuthScreenshotAvailable || shouldShowDiagnosticBundle(profile?.lastScanAuthStatus ?? null, profile?.lastScanAuthScreenshotAvailable ?? false)) && (
                <div style={{ marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {profile?.lastScanAuthScreenshotAvailable && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => void handlePreviewDiagnosticScreenshot('scan')}
                      disabled={openingScreenshotScope !== null}
                    >
                      {openingScreenshotScope === 'scan' ? t('authProfile.actions.loadingPreview') : t('authProfile.actions.previewScreenshot')}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void handleDownloadDiagnostics('scan')}
                    disabled={downloadingDiagnosticScope !== null}
                  >
                    {downloadingDiagnosticScope === 'scan' ? t('authProfile.actions.downloading') : t('authProfile.actions.downloadDiagnostics')}
                  </Button>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <div>
                {profile && (
                  <Button type="button" variant="danger" onClick={() => void handleRemove()} disabled={removing}>
                    {removing ? t('authProfile.actions.removing') : t('authProfile.actions.removeProfile')}
                  </Button>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Button type="button" variant="secondary" onClick={() => void handleTest()} disabled={testing || saving || removing}>
                  {testing ? t('authProfile.actions.testing') : t('authProfile.actions.testAccess')}
                </Button>
                <Button type="button" onClick={() => void handleSave()} disabled={saving || testing || removing}>
                  {saving ? t('authProfile.actions.saving') : t('authProfile.actions.saveProfile')}
                </Button>
              </div>
            </div>

            {previewScope && previewUrl && (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(17, 24, 39, 0.72)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 24,
                  zIndex: 1000,
                }}
                onClick={clearPreview}
              >
                <div
                  style={{
                    background: 'var(--card-bg)',
                    borderRadius: 14,
                    maxWidth: 980,
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'hidden',
                    display: 'grid',
                    gap: 0,
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div
                    style={{
                      padding: '14px 18px',
                      borderBottom: '1px solid var(--acc-color-border)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--acc-color-text)' }}>
                        {previewScope === 'test' ? t('authProfile.preview.testTitle') : t('authProfile.preview.scanTitle')}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)' }}>
                        {t('authProfile.preview.subtitle')}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}
                      >
                        {t('authProfile.actions.openFullSize')}
                      </Button>
                      <Button type="button" variant="secondary" onClick={clearPreview}>
                        {t('common:actions.close')}
                      </Button>
                    </div>
                  </div>
                  <div style={{ padding: 18, background: 'var(--surface-secondary)', overflow: 'auto' }}>
                    <img
                      src={previewUrl}
                      alt={previewScope === 'test' ? t('authProfile.preview.testAlt') : t('authProfile.preview.scanAlt')}
                      style={{ width: '100%', height: 'auto', borderRadius: 10, display: 'block' }}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
